import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {TUTORIAL_ROUTES} from 'constants/TutorialConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {EntityWithText} from 'ui-library/entity-input/EntityInputConstants';
import {LegacyEntityInput} from 'ui-library/entity-input/LegacyEntityInput';
import HelpIcon from 'ui-library/icons/HelpIcon';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {TextAreaChangeEvent} from 'util/Types';

import {EsignatureStatement} from './EsignatureStatement';
import {ReviewComment} from './ReviewComment';
import {ReviewAction} from './ReviewConstants';

interface CreateUpdateReviewModalBodyProps {
  auditors: Array<EntityWithText>;
  buttonText: string;
  comment: string | null;
  error: string;
  handleAuditorChange: (entities: Array<EntityWithText>) => void;
  handleCommentChange: (e: TextAreaChangeEvent) => void;
  isSelfReview: boolean;
  reviewAction: ReviewAction | null;
  selectedAuditors: Array<EntityWithText>;
  shouldRenderCommentBox: boolean;
}

/*
This modal is used to create (request) reviews.
It is not used to accept, reject, retract, or re-send reviews that already exist -- that is ReviewStatusModal.
*/
const CreateUpdateReviewModalBody = React.forwardRef<HTMLTextAreaElement, CreateUpdateReviewModalBodyProps>(
  (props, ref) => {
    function _renderCommentBox(): JSX.Element | null {
      return props.shouldRenderCommentBox ? (
        <ReviewComment
          ref={ref}
          comment={props.comment ?? undefined}
          onChange={props.handleCommentChange}
          labelClassName="u-spacing-top"
          rowCount={20}
          disabled={!BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS}
          shouldAutoFocus={true}
          showRedAsterisk={false}
        />
      ) : null;
    }

    const _getFilterOptions = (
      inputText: string,
      suggestions: Array<EntityWithText>
    ): Array<EntityWithText> =>
      inputText.length === 0
        ? suggestions
        : suggestions.filter(({handle, name = '', text = ''}) => {
            const re = new RegExp(inputText, 'i');
            return [handle, name, text].some((value) => re.test(value));
          });

    const esigInfo = <EsignatureStatement action={props.reviewAction} buttonText={props.buttonText} />;

    return (
      <div>
        {props.error ? <div className="error-msg u-spacing-bottom">{props.error}</div> : undefined}
        {esigInfo}
        {!props.isSelfReview && (
          <Row>
            <Col md={3}>
              <h6 className="faded u-display-inline-block">
                <span className="all-caps">Auditors</span>{' '}
                <a href={TUTORIAL_ROUTES.ELN_ADDING_AUDITORS} target="_blank" rel="noopener noreferrer">
                  <HelpIcon />
                </a>
              </h6>
            </Col>
            <Col md={9}>
              <LegacyEntityInput
                size={TagsInputSize.SMALL}
                allowAdjustInputWidth={false}
                placeholder="Search for a user"
                allOptions={props.auditors}
                filterOptions={_getFilterOptions}
                value={props.selectedAuditors}
                onChange={props.handleAuditorChange}
                dataTestAttrs={{component: 'CreateUpdateReviewModal', element: 'body', key: 'auditors'}}
              />
            </Col>
          </Row>
        )}
        {_renderCommentBox()}
        {window.impersonating ? (
          <strong style={{color: 'red'}}>
            DO NOT CLICK THE SUBMIT BUTTON WHILE IMPERSONATING A REAL USER
          </strong>
        ) : null}
      </div>
    );
  }
);

export default CreateUpdateReviewModalBody;
